import { useState } from 'react';
import type { JSX, KeyboardEvent } from 'react';
import { useCheckLoop } from '../hooks/useCheckLoop';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { useWakeLock } from '../hooks/useWakeLock';
import { useI18n } from '../i18n';
import type { StringKey } from '../i18n';
import { routes, shouldNavigate, startRoute } from '../lib/tabNavigation';
import type { TabRoute } from '../lib/tabNavigation';
import { HomeScreen } from './HomeScreen';
import { ResultScreen } from './ResultScreen';
import { SettingsScreen } from './SettingsScreen';
import { AppBackground } from './ui/AppBackground';
import { stepIndex } from './ui/Dialog';
import { Icon } from './ui/Icon';
import type { IconName } from './ui/Icon';
import { cx } from './ui/cx';
import { useLanguageSheet } from './ui/useLanguageSheet';

/**
 * The signed-in shell — a port of `ui/navigation/MainShell.kt`.
 *
 * Three peer tabs, one crossfade, one bottom bar, and **one** of everything the
 * three tabs share. That last part is the reason this file exists rather than
 * each screen fetching what it needs: `useCheckLoop` owns a scheduler, an
 * in-flight cycle and a "one cycle at a time" guard, all of them in refs. A
 * second instance would not share those refs — it would run its own timer, its
 * own cycle and its own `POST /device-api/results`, which breaks the hardest
 * invariant this app has (exactly one submit per cycle) in a way that looks
 * perfectly healthy from the backend's side: two well-formed batches, minutes
 * apart, from one device. Hoisting the hook here is what makes switching tabs
 * free; mounting it per screen would restart a run on every tap.
 *
 * ### Android's UpdateGate is not here, and never will be
 *
 * `AppNav.kt` wraps this shell in `ui/update/UpdateGate`, which polls
 * `app-config`, compares versions and can block the app behind a "download the
 * new APK" sheet. A WebClip has no APK and no version to be behind: the icon
 * opens a URL, and whatever the host is serving at that moment *is* the app —
 * an update ships by deploying, and the device picks it up on its next load. A
 * gate here would guard against a state that cannot occur, and `update_*` is
 * why none of those strings were ported.
 */
export function MainShell({
  deviceKey,
  onKeyRevoked,
  onKeyRemoved,
}: {
  deviceKey: string;
  onKeyRevoked(): void;
  onKeyRemoved(): void;
}): JSX.Element {
  /**
   * Which tab is showing. The whole navigation model of this shell, exactly as
   * on Android — one string, no history, no back stack.
   *
   * Not persisted anywhere. Android keeps it in `rememberSaveable` because a
   * configuration change or process death would otherwise drop it mid-session;
   * a WebClip reload is not that — it is the app starting over — and an operator
   * who reopens the icon to find Settings instead of the dashboard has to work
   * out where the numbers went.
   */
  const [route, setRoute] = useState<TabRoute>(startRoute);

  /** One sheet behind all three tabs — see `ui/useLanguageSheet.tsx`. */
  const language = useLanguageSheet();

  const loop = useCheckLoop(deviceKey);
  const status = useNetworkStatus();

  /**
   * Keep the screen awake for as long as the shell is mounted.
   *
   * This used to hang off `Dashboard`, which the port replaced; the call moved
   * here rather than into `HomeScreen` because the lock has to survive a tab
   * switch. Scoped to a screen it would be dropped and re-requested on every
   * tap, and released entirely while the operator reads Settings — the exact
   * moment a cycle is most likely to be running unattended.
   *
   * Best-effort only, and not a fix for SPEC §14 #7: the app still dies when
   * the screen goes off. The provisioning steps this app prints in Settings
   * (`ios_setup_autolock` and friends) remain the real guarantee.
   */
  useWakeLock(true);

  /**
   * Re-selecting the current tab is a no-op — the rule lives in
   * `src/lib/tabNavigation.ts` and is asserted there. It matters more here than
   * it does on Android: this shell keeps every tab mounted, so a re-selection
   * that fell through would restart the 200ms fade on the pane the user is
   * already reading, for no reason they could name.
   *
   * The functional `setRoute` is the part that has to stay: the rule is about
   * the route we are on *now*, and a tap handler that closed over a stale one
   * would let a queued second tap re-enter the tab it was already leaving.
   *
   * Not memoised, and neither are the two below it. There is no `React.memo`
   * anywhere in `src/`, and all three panes re-render on every loop dispatch
   * regardless — a `useCallback` here would buy a stable identity that nothing
   * in the tree is watching for.
   */
  const switchTo = (target: TabRoute): void => {
    setRoute((current) => (shouldNavigate(current, target) ? target : current));
  };

  return (
    <AppBackground>
      {/*
       * All three panes stay mounted, two of them hidden.
       *
       * This is the web half of Android's `rememberSaveableStateHolder`: it is
       * what keeps the Home scroll position, the Results filter and the toast
       * timer alive across a trip to Settings and back. Unmounting the inactive
       * tabs would be cheaper and is the obvious thing to write — and it throws
       * away the filter every time somebody glances at the dashboard, which is
       * the papercut the Android version restructured its navigation to fix.
       *
       * The crossfade needs no JavaScript. `[hidden]` resolves to
       * `display: none`, and an element that goes from `display: none` to
       * displayed starts its CSS animations from the beginning — so `.tab-pane`'s
       * `tab-fade` runs on the incoming pane every time, at MainShell.kt's 200ms.
       * Fade-through rather than a slide, because with three peer tabs a
       * horizontal slide implies an order that does not exist.
       */}
      {TABS.map((tab) => {
        const active = tab.route === route;
        return (
          <div
            key={tab.route}
            id={panelId(tab.route)}
            className="tab-pane"
            role="tabpanel"
            aria-labelledby={tabId(tab.route)}
            hidden={!active}
          >
            {tab.route === 'home' ? (
              <HomeScreen
                loop={loop}
                status={status}
                // Every pane stays mounted, so nothing else tells a hidden one
                // that it is hidden — and `LastCheckBanner` ticks once a second.
                // A countdown nobody can see is a wake-up per second on a field
                // phone that is already running the app off its own battery.
                active={active}
                onViewResults={() => switchTo('result')}
                onOpenLanguage={language.open}
                // A `401` mid-run. Handed straight up to `App.tsx`, which returns
                // to the gate — Android's `HomeRoute` → `onNeedKey` → `AppNav`,
                // the same path. The dashboard does not lock itself.
                onKeyRevoked={onKeyRevoked}
              />
            ) : tab.route === 'result' ? (
              // `loop.shown` rather than a second copy of the pick-a-list rule
              // (`src/lib/shownResults.ts`): "See all" is a promise that this
              // tab is the long version of the four rows on Home, so the two
              // must resolve the same list — from the same place.
              <ResultScreen results={loop.shown} />
            ) : (
              <SettingsScreen
                deviceKey={deviceKey}
                loop={loop}
                status={status}
                onOpenLanguage={language.open}
                onKeyRemoved={onKeyRemoved}
              />
            )}
          </div>
        );
      })}

      <BottomBar current={route} onSelect={switchTo} />

      {language.sheet}
    </AppBackground>
  );
}

/**
 * The tab bar.
 *
 * A `<div role="tablist">` of `<button role="tab">`s rather than a `<nav>`: the
 * tab roles are what tell VoiceOver that these three controls swap one region
 * of the page, and `role="tablist"` on a `<nav>` would override the landmark it
 * was written for anyway.
 *
 * There is deliberately **no** `aria-label` on the list. Every ported string
 * comes from Android's `strings.xml` and there is no navigation-bar label among
 * them; writing one here would ship English text to the three locales that have
 * no translation of it, and an unlabelled tablist is announced correctly by its
 * role and its three named tabs regardless.
 */
function BottomBar({
  current,
  onSelect,
}: {
  current: TabRoute;
  onSelect(route: TabRoute): void;
}): JSX.Element {
  const { t } = useI18n();

  /**
   * Arrow keys move between tabs, matching the roving tabindex below: the bar is
   * one Tab stop, not three. Only reachable from an external keyboard — there is
   * nothing here for touch or VoiceOver, which walk the buttons themselves —
   * but a bar that swallows Tab without offering the arrows is a trap for the
   * one operator who has a keyboard paired.
   *
   * The wrap-around itself is `stepIndex`, shared with the language sheet's
   * radio group: both are a roving-tabindex row where the arrows have to come
   * back round, and an off-by-one in either is invisible until somebody with a
   * keyboard reaches the last item.
   */
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const index = stepIndex(event.key, routes.indexOf(current), routes.length);
    if (index === null) return;

    const next = routes[index];
    if (next === undefined) return;
    event.preventDefault();
    onSelect(next);
    document.getElementById(tabId(next))?.focus();
  };

  return (
    <div className="bottom-bar" role="tablist" onKeyDown={onKeyDown}>
      {TABS.map((tab) => {
        const selected = tab.route === current;
        return (
          <button
            key={tab.route}
            id={tabId(tab.route)}
            type="button"
            role="tab"
            aria-selected={selected}
            aria-controls={panelId(tab.route)}
            // Roving tabindex: tabbing into the bar lands on the tab you are on,
            // not on the first one, so the focus ring never claims a screen the
            // user is not looking at.
            tabIndex={selected ? 0 : -1}
            className={cx('bottom-bar__item', selected && 'bottom-bar__item--selected')}
            onClick={() => onSelect(tab.route)}
          >
            {/* The gradient pill Material's own indicator cannot draw, so
                Android puts it in the icon slot — same here. It is always in the
                tree and only painted when selected, so the icon does not shift
                by a pixel as the selection moves. */}
            <span className="bottom-bar__indicator">
              <Icon name={tab.icon} size={26} className="bottom-bar__icon" />
            </span>
            <span className="bottom-bar__label t-label-medium clamp-2 break-anywhere">{t(tab.label)}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * One row per tab: what the bar draws, in the order it draws it.
 *
 * `MainShell.kt` writes this as two lookups mapped over `TabNavigation.routes`,
 * and the port copied the shape — three entries spread over three declarations,
 * with a `.map` at the end to put them back together. One literal says the same
 * thing, and the type below keeps the guarantee that shape was there for: the
 * tuple is built *from* `routes`, so it must have exactly one entry per tab, in
 * the same order. Adding a route without giving it an icon and a label is a type
 * error rather than a blank slot in the bar — and a bar whose order has drifted
 * from the navigation list's is a type error too, which the two `Record`s could
 * not catch.
 */
type TabsFor<R extends readonly TabRoute[]> = {
  [K in keyof R]: { route: R[K]; icon: IconName; label: StringKey };
};

const TABS: TabsFor<typeof routes> = [
  { route: 'home', icon: 'speed', label: 'nav_dashboard' },
  { route: 'result', icon: 'fact-check', label: 'nav_results' },
  { route: 'settings', icon: 'settings', label: 'nav_settings' },
];

/** Stable ids so each tab and its panel can point at each other. */
function tabId(route: TabRoute): string {
  return `tab-${route}`;
}

function panelId(route: TabRoute): string {
  return `panel-${route}`;
}
